<template>
  <div
    id="root"
    v-loading="isSaving"
    element-loading-text="Saving..."
    element-loading-spinner="el-icon-loading"
  >
    <div class="draggable-card-title">
      {{ record.person }}
      <div class="float-right">
        <i
          :class="{ hide: editing || !allowEditing }"
          class="el-icon-edit icon-btn"
          @click="$emit('setEditRecordId', record.id)"
        />
      </div>
    </div>
    <div class="container draggable-card-body">
      <div class="row">
        <div class="col-md-3 min-lr-padding">
          <div
            :class="{
              hide: editing,
              placeholder: !record.phone,
              'fit-text': true
            }"
          >
            {{ returnPhoneOrPlaceholder(record.phone) }}
          </div>
          <el-input
            :class="{ hide: !editing }"
            class="fit-input"
            placeholder="Phone"
            v-model="phone"
            style="margin:0"
          />
        </div>
        <div class="col-md-3 min-lr-padding">
          <div
            :class="{
              hide: editing,
              placeholder: !record.email,
              'fit-text': true
            }"
          >
            {{ returnEmailOrPlaceholder(record.email) }}
          </div>
          <el-input
            :class="{ hide: !editing }"
            class="fit-input"
            type="text"
            placeholder="Email"
            v-model="email"
            style="margin:0"
          />
        </div>
        <div class="col-md-4 min-lr-padding" style="margin:0">
          <div
            :class="{
              hide: editing || !returnDateOrPlaceholder(record),
              placeholder: !record.graduation_month,
              'fit-text': true
            }"
          >
            {{ returnDateOrPlaceholder(record) }}
          </div>
          <el-select
            :class="{ hide: !editing }"
            class="select-default light-gray fit-input"
            style="width: 50%; margin:0;"
            v-model="graduationMonth"
          >
            <el-option
              v-for="option in graduationMonthOptions"
              :key="option.name"
              :label="option.name"
              :value="option.name"
            />
          </el-select>
          <el-select
            :class="{ hide: !editing }"
            class="select-default light-gray fit-input"
            style="width: 50%; margin:0;"
            v-model="graduationYear"
          >
            <el-option
              v-for="option in graduationYearOptions"
              :key="option"
              :label="option"
              :value="option"
            />
          </el-select>
        </div>
        <div class="col-md-2 min-lr-padding">
          <div
            :class="{
              hide: editing,
              'fit-text': true
            }"
          >
            {{ returnVocationOrPlaceholder(record.vocation) }}
          </div>
          <div :class="{ hide: !editing }">
            Vocation
          </div>
          <div style="display:inline-block;">
            <el-radio
              :class="{ hide: !editing }"
              v-model="toggleVocation"
              :label="true"
            >
              Yes
            </el-radio>
            <el-radio
              :class="{ hide: !editing }"
              v-model="toggleVocation"
              :label="false"
            >
              No
            </el-radio>
          </div>
        </div>
      </div>
      <div
        class="row"
        :class="{
          hide: !editing
            || !toggleVocation
        }"
        style="margin-top: 20px;"
      >
        <div class="col-md-6" style="text-align:center;">
          Entering religious life or seminary?
          <div style="margin-top: 20px;">
            <el-radio v-model="religiousOrSeminary" label="Religious" />
            <el-radio v-model="religiousOrSeminary" label="Seminary" />
          </div>
        </div>
        <div class="col-md-6">
          Diocese or order:
          <el-input
            placeholder="Diocese or order"
            v-model="dioceseOrOrder"
          />
        </div>
      </div>
      <div class="row">
        <div class="col-md-12 min-lr-padding">
          <i
            :class="{ hide: !editing }"
            class="el-icon-circle-check icon-btn lrg float-right"
            @click="handleValidateBeforeSave()"
          />
        </div>
      </div>
      <div class="row">
        <div class="col-md-12 draggable-card-footer horizontal">
          <span>{{ record.involvement.involvement }}</span>
          <span>Leader: {{ record.involvement.bs_leader_discipler }}</span>
          <span>Missionary: {{ record.involvement.missionary }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { RepositoryFactory } from '../../repositories/repositoryFactory';

const GraduationRecordsRepository = RepositoryFactory.get('graduationRecords');

export default {
    name: 'RecordForm',
    props: {
        editing: {
            type: Boolean,
            default: false,
        },
        allowEditing: {
            type: Boolean,
            default: false,
        },
        record: {
            type: Object,
            default: null
        },
        graduationMonthOptions: {
            type: Array,
            default() { return []; }
        },
        graduationYearOptions: {
            type: Array,
            default() { return []; }
        }
    },
    data() {
        return {
            id: null,
            phone: null,
            email: null,
            graduationMonth: null,
            graduationYear: null,
            toggleVocation: false,
            religiousOrSeminary: null,
            dioceseOrOrder: null,
            validationErrors: {
                invalidPhone: null,
                invalidEmail: null,
                invalidYear: null,
            },
            isSaving: false,
        };
    },
    watch: {
        editing() {
            this.id = this.record.id;
            this.phone = this.record.phone;
            this.email = this.record.email;
            if (!this.record.graduation_month) {
                this.graduationMonth = this.graduationMonthOptions[0].name;
            } else {
                this.graduationMonth = this.record.graduation_month;
            }
            if (!this.record.graduation_year) {
                [this.graduationYear] = this.graduationYearOptions;
            } else {
                this.graduationYear = this.record.graduation_year;
            }
            this.toggleVocation = (this.record.vocation.is_entering_religious
              || this.record.vocation.is_entering_seminary);
            if (this.record.vocation.is_entering_religious === true) {
                this.religiousOrSeminary = 'Religious';
            } else if (this.record.vocation.is_entering_seminary === true) {
                this.religiousOrSeminary = 'Seminary';
            } else {
                this.religiousOrSeminary = null;
            }
            this.dioceseOrOrder = this.record.vocation.diocese_or_order;
        },
        toggleVocation() {
            if (this.toggleVocation === true) {
                if (this.record.vocation.is_entering_religious === true) {
                    this.religiousOrSeminary = 'Religious';
                } else if (this.record.vocation.is_entering_seminary === true) {
                    this.religiousOrSeminary = 'Seminary';
                } else {
                    this.religiousOrSeminary = null;
                }
            } else {
                this.religiousOrSeminary = null;
                this.dioceseOrOrder = null;
            }
        }
    },
    methods: {
        returnEmailOrPlaceholder(email) {
            if (!email) {
                return '[Email]';
            }
            return email;
        },
        returnPhoneOrPlaceholder(phone) {
            if (!phone) {
                return '[Phone]';
            }
            return phone;
        },
        returnDateOrPlaceholder(record) {
            if (!record.graduation_month && !record.graduation_year) {
                return '[Graduation date]';
            }
            return `${record.graduation_month} ${record.graduation_year}`;
        },
        returnVocationOrPlaceholder(vocation) {
            if (vocation.is_entering_seminary) {
                return 'Vocation: seminary';
            }
            if (vocation.is_entering_religious) {
                return 'Vocation: religious';
            }
            return 'No vocation';
        },
        handleValidateBeforeSave() {
            // https://www.w3resource.com/javascript/form/email-validation.php
            // Must not be a .edu email
            const emailFormat = '^[a-zA-Z0-9.!#$%&\'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+((?!\.edu)(?:\.[a-zA-Z0-9-]+)(?!\.edu))+$';
            if (this.email && !this.email.match(emailFormat)) {
                this.validationErrors.invalidEmail = 'email (must be non .edu), ';
            } else {
                this.validationErrors.invalidEmail = '';
            }
            // https://www.w3resource.com/javascript/form/phone-no-validation.php
            const phoneFormat = /^\(?([0-9]{3})\)?[- ]?([0-9]{3})[- ]?([0-9]{4})$/;
            if (this.phone && !this.phone.match(phoneFormat)) {
                this.validationErrors.invalidPhone = 'phone, ';
            } else {
                this.validationErrors.invalidPhone = '';
            }
            // Valid years: 2000-2099
            const yearFormat = /^(20)\d{2}$/;
            if (this.graduationYear && !String(this.graduationYear).match(yearFormat)) {
                this.validationErrors.invalidYear = 'graduation year, ';
            } else {
                this.validationErrors.invalidYear = '';
            }
            if (!this.validationErrors.invalidEmail
              && !this.validationErrors.invalidPhone
              && !this.validationErrors.invalidYear
            ) {
                this.handleSaveEditingRecord();
            } else {
                let alert = `The following fields are invalid: ${this.validationErrors.invalidEmail}${this.validationErrors.invalidPhone}${this.validationErrors.invalidYear}`;
                alert = alert.slice(0, -2);
                this.$alert(alert);
                this.$message(alert);
            }
        },
        handleSaveEditingRecord() {
            this.isSaving = true;
            let seminary = false;
            let religious = false;
            if (this.religiousOrSeminary === 'Religious') {
                religious = true;
            } else if (this.religiousOrSeminary === 'Seminary') {
                seminary = true;
            }
            GraduationRecordsRepository.updateGraduationRecords(
                [
                    {
                        id: this.id,
                        phone: this.phone,
                        email: this.email,
                        graduation_month: this.graduationMonth,
                        graduation_year: this.graduationYear,
                        graduation_status: 'graduating',
                        vocation: {
                            is_entering_religious: religious,
                            is_entering_seminary: seminary,
                            diocese_or_order: this.dioceseOrOrder
                        }
                    }
                ]
            )
                .then((response) => {
                    this.$emit('setGraduationRecords', response.data);
                    if (this.phone && this.email
                      && this.graduationMonth && this.graduationYear) {
                        this.$message('This student\'s information is complete. Thanks!');
                    }
                    this.id = null;
                    this.phone = null;
                    this.email = null;
                    this.graduationMonth = this.graduationMonthOptions[0].name;
                    [this.graduationYear] = this.graduationYearOptions;
                    this.toggleVocation = null;
                    this.religiousOrSeminary = null;
                    this.dioceseOrOrder = null;
                    this.isSaving = false;
                })
                .catch((error) => {
                    // Bugsnag - cannot read response of undefined
                    this.$message(error.response.data.message);
                    this.isSaving = false;
                });
        },
    }
};
</script>

<style>
.placeholder {
  color: gray;
  background-color: #FFD1D1;
  border-radius: 5px;
}
.flat-right {
  border-radius: 5px 0 0 5px;
}
.flat-left {
  border-radius: 0 5px 5px 0;
}
/* Override element-ui styling */
.el-radio-button:last-child .el-radio-button__inner {
  border-left: 1px solid #DCDFE6 !important;
}
.el-radio-button:last-child .el-radio-button__inner {
  border-left: 1px solid #DCDFE6 !important;
}
</style>
